import { type InfiniteData, type QueryClient, type QueryKey } from '@tanstack/react-query';

import { type ClipsListResponse } from '@air/api';
import { type Board, type Clip, type ListResponse } from '@air/api/types';

import { GALLERY_ASSETS, GALLERY_BOARDS, GALLERY_FILES } from '~/constants/react-query-keys';
import { ASSET_TYPES, FILE_TYPES } from '~/swr-hooks/gallery/galleryView/utilsCollection';

export type InsertItemsIntoCollectionGalleryQueriesParams = {
  queryClient: QueryClient;
  collectionId: string;
  /**
   * Which sub-page's public/canvas gallery to write into — the SAME value
   * usePublicShowcaseGalleryViewItems spreads into its cache key: `undefined` is the root/Home
   * page (whose key omits the param entirely), any collectionId is that sub-page.
   *
   * Optional and defaulting to `undefined` on purpose: every caller that predates sub-pages (board
   * pages, the private FOUND-477 path) omits it and lands on the root key only — byte-identical to
   * the single-gallery world before sub-pages existed. Editor call-sites pass the active page so an
   * optimistic insert on one sub-page can't bleed into Home or a sibling sub-page.
   */
  subPageCollectionId?: string;
  boards: Board[];
  clips: Clip[];
};

/**
 * Writes just-added items into every cached collection gallery page (boards / assets / files,
 * across both the private `{ collectionId: '<uuid>' }` and public/canvas `{ collectionId: true }`
 * key shapes — see invalidateCollectionGalleryItemQueries for the shape split).
 *
 * This exists because the public shortId listing endpoints' reads lag the add mutation — an
 * immediate refetch can come back WITHOUT the just-added items, leaving the canvas unchanged until
 * a manual refresh (FOUND-477). The add call-sites that render a gallery hold the FULL entities
 * they just added, so writing those into the cache beats refetching a known-laggy source: the
 * mirror of pruneCollectionGalleryItemQueries on the remove side (FOUND-478).
 *
 * Clips are routed into the assets vs files caches by the SAME type partition the category
 * fetchers request (`types: ASSET_TYPES` vs `types: FILE_TYPES` in utilsCollection), so an
 * inserted file lands in the files gallery, not the assets one. Entities already present in a
 * cached page are skipped (a re-add, or a natural refetch that already delivered the item, must
 * not duplicate its card); the rest are appended to the LAST page's data and every page's `total`
 * is bumped by the inserted count — the mirror of prune's decrement.
 *
 * Placement (product-specified): inserted clips go to the FRONT of the first page; inserted
 * boards are APPENDED to the last page. Either way the card sits at its inserted position rather
 * than its server-sorted one until the next natural refetch.
 */
export const insertItemsIntoCollectionGalleryQueries = ({
  queryClient,
  collectionId,
  subPageCollectionId,
  boards,
  clips,
}: InsertItemsIntoCollectionGalleryQueriesParams) => {
  const matchesCollectionGalleryKey =
    (mainKey: string) =>
    ({ queryKey }: { queryKey: QueryKey }) => {
      const [key, keyParams] = queryKey;

      if (key !== mainKey) {
        return false;
      }

      if (typeof keyParams !== 'object' || keyParams === null || !('collectionId' in keyParams)) {
        return false;
      }

      // Private page (`{ collectionId: '<uuid>' }`): matched exactly as before — sub-pages don't
      // touch this shape, so it stays byte-identical to the pre-sub-page behavior.
      if (keyParams.collectionId === collectionId) {
        return true;
      }

      // Public/canvas page (`{ collectionId: true, shortId, subPageCollectionId? }`): a bare
      // `=== true` used to match EVERY such gallery, so an optimistic insert on one page landed in
      // Home's and every sibling sub-page's cache too (a visible flash until the scoped refetch
      // corrected it). Scope the OPTIMISTIC write to the target page instead: the key omits
      // `subPageCollectionId` on root, and root callers pass `undefined`, so both sides collapse to
      // `undefined` there and only the root key matches — unchanged for every pre-sub-page caller.
      // (Invalidation stays broad on `=== true` elsewhere: a refetch is correctly per-page scoped.)
      if (keyParams.collectionId === true) {
        const keySubPageCollectionId = 'subPageCollectionId' in keyParams ? keyParams.subPageCollectionId : undefined;

        return keySubPageCollectionId === subPageCollectionId;
      }

      return false;
    };

  if (boards.length) {
    queryClient.setQueriesData<InfiniteData<ListResponse<Board>>>(
      { predicate: matchesCollectionGalleryKey(GALLERY_BOARDS) },
      (cached) => {
        if (!cached || !cached.pages.length) {
          return cached;
        }

        const cachedBoardIds = cached.pages.flatMap((page) => page.data.map((board) => board.id));
        const boardsToInsert = boards.filter((board) => !cachedBoardIds.includes(board.id));

        if (!boardsToInsert.length) {
          return cached;
        }

        const lastPageIndex = cached.pages.length - 1;

        return {
          ...cached,
          pages: cached.pages.map((page, pageIndex) => ({
            ...page,
            data: pageIndex === lastPageIndex ? [...page.data, ...boardsToInsert] : page.data,
            total: page.total + boardsToInsert.length,
          })),
        };
      },
    );
  }

  const clipInserts = [
    { mainKey: GALLERY_ASSETS, clipsForKey: clips.filter((clip) => ASSET_TYPES.includes(clip.type)) },
    { mainKey: GALLERY_FILES, clipsForKey: clips.filter((clip) => clip.type === FILE_TYPES) },
  ];

  for (const { mainKey, clipsForKey } of clipInserts) {
    if (!clipsForKey.length) {
      continue;
    }

    queryClient.setQueriesData<InfiniteData<ClipsListResponse<Clip>>>(
      { predicate: matchesCollectionGalleryKey(mainKey) },
      (cached) => {
        if (!cached || !cached.pages.length) {
          return cached;
        }

        const cachedClipIds = cached.pages.flatMap((page) => page.data.clips.map((clip) => clip.id));
        const clipsToInsert = clipsForKey.filter((clip) => !cachedClipIds.includes(clip.id));

        if (!clipsToInsert.length) {
          return cached;
        }

        return {
          ...cached,
          pages: cached.pages.map((page, pageIndex) => ({
            ...page,
            data: {
              ...page.data,
              // Clips lead their gallery (product-specified), unlike boards, which append.
              clips: pageIndex === 0 ? [...clipsToInsert, ...page.data.clips] : page.data.clips,
              total: page.data.total + clipsToInsert.length,
            },
          })),
        };
      },
    );
  }
};
